// @flow

import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {makeStyles, Typography, IconButton} from '@material-ui/core';

import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {Delete} from '@material-ui/icons';

import type {Theme} from '@material-ui/core';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {
  getConversationDisplayName,
  useCurrentConversation,
} from '../../utils/conversation';
import {updateConversationService} from '../../promises/conversation';
import {useToastActions} from '../../../../toast-alert/utils';
import {useActions} from '../../../../../utils/globalStore';
import {actions} from '../../duck';
type Props = {
  open: boolean,
  handleClose: () => void,
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 320,
    maxWidth: '100%',
  },
  title: {
    fontWeight: 600,
    fontSize: 18,
    color: colors.grey900,
  },
  button: {
    marginBottom: theme.spacing(2),
  },
  content: {
    margin: theme.spacing(3, 0, 2, 0),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

export default function EditCustomerModal({open, handleClose}: Props) {
  const classes = useStyles();

  const {currentConversation: conversation} = useCurrentConversation();
  const {showToast} = useToastActions();
  const {updateStoreConversation} = useActions({
    updateStoreConversation: actions.updateStoreConversation,
  });
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const conversationRef = useRef(conversation);

  const displayName = conversation
    ? getConversationDisplayName(conversation)
    : '';

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  useEffect(() => {
    if (!open || !conversation) return;
    setName(displayName);
  }, [conversation, displayName, open]);

  if (!conversation) return null;

  const trimmedName = name.trim();
  const hasCustomName = Boolean(conversation.contactInformation?.contactName);

  const handleSave = () => {
    if (!trimmedName || loading) return;
    const conv = conversationRef.current;
    setLoading(true);
    updateConversationService(conv.id, {contactName: trimmedName})
      .then(() => {
        updateStoreConversation({
          id: conv.id,
          contactInformation: {
            ...conv.contactInformation,
            contactName: trimmedName,
          },
        });
        handleClose();
      })
      .catch(error => {
        console.error(error);
        showToast({
          title: 'Error',
          message: 'No se pudo actualizar el nombre del contacto',
          type: 'danger',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteName = () => {
    if (loading) return;
    const conv = conversationRef.current;
    setLoading(true);
    updateConversationService(conv.id, {contactName: ''})
      .then(() => {
        updateStoreConversation({
          id: conv.id,
          contactInformation: {
            ...conv.contactInformation,
            contactName: '',
          },
        });
        setName('');
        handleClose();
      })
      .catch(error => {
        console.error(error);
        showToast({
          title: 'Error',
          message: 'No se pudo eliminar el nombre del contacto',
          type: 'danger',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      <div className={classes.root}>
        <Typography className={classes.title} align="center">
          Información de cliente
        </Typography>
        <div className={classes.content}>
          <form
            noValidate
            className={classes.form}
            onSubmit={event => {
              event.preventDefault();
              handleSave();
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
              <CustomTextField
                label="Nombre"
                name="name"
                value={name}
                onChange={event => setName(event.target.value)}
                style={{flex: 1}}
              />
              {hasCustomName && (
                <IconButton
                  onClick={handleDeleteName}
                  size="small"
                  disabled={loading}
                  type="button"
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </div>
            <CustomButton
              fullWidth
              variant="contained"
              className={classes.button}
              disabled={!trimmedName || loading}
              type="submit"
            >
              Guardar
            </CustomButton>
          </form>
        </div>
      </div>
    </CustomModal>
  );
}

EditCustomerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};
